/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";

import { FaTimes } from "react-icons/fa";

import { MyButton } from "./ButtonUtils";
import { useCurrencyContext } from "../../context/CurrencyContext";
import { getCountry } from "../../lib/sysNavigation/SysNavigation";
import { getOneLevel } from "../../lib/breadCrumb/BreadCrumb";
import { useGeoPositionContext } from "../doseeData/utils/geoPosition";

const Pricing = ({ pricing, setPricing }: { pricing?: any; setPricing?: any }) => {
  const { currency } = useGeoPositionContext();

  return (
    <div className="w-full py-5">
      {/* List */}
      <div className="grid grid-cols-1 gap-5">
        {(pricing || []).length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
            <p className="text-sm text-gray-700">
              No pricing items yet. Add your first price or fee below.
            </p>
          </div>
        ) : (
          pricing?.map((price, key) => (
            <PricingComponent
              key={key}
              index={key}
              title={`Pricing Details • ${String(key + 1).padStart(2, "0")}`}
              name={price.name}
              pricing={pricing}
              amount={price.amount}
              setPricing={setPricing}
              currency={price.currency}
            />
          ))
        )}
      </div>

      {/* Add new */}
      <div className="mt-6 border-t border-gray-100 pt-6">
        <MyButton
          type="button"
          onClick={() => {
            setPricing([
              ...pricing,
              {
                name: "",
                currency: currency,
                amount: 0,
                quantity: 1,
                status: 1,
              },
            ]);
          }}
          className="bg-gray-900 hover:bg-gray-800 text-white text-sm rounded-xl px-5 py-3 shadow-sm"
        >
          Add More Prices / Fees
        </MyButton>
      </div>
    </div>
  );
};

export default Pricing;

const PricingComponent = ({
  name,
  title,
  index,
  amount,
  pricing,
  currency,
  setPricing,
}: {
  name?: any;
  title?: any;
  index?: any;
  amount?: any;
  pricing?: any;
  currency?: any;
  setPricing?: any;
}) => {
  const removePrice = () => {
    const arr = pricing.filter(
      (item) => !(item?.name === name && item?.currency === currency)
    );
    setPricing(arr);
  };

  const setPriceName = (name) => {
    const data = pricing.map((x) => x);
    data[index] = { ...data[index], name };
    setPricing(data);
  };

  const setCurrencyCode = (currency) => {
    const data = pricing.map((x) => x);
    data[index] = { ...data[index], currency };
    setPricing(data);
  };

  const setAmount = (amount) => {
    const data = pricing.map((x) => x);
    data[index] = { ...data[index], amount };
    setPricing(data);
  };

  const prettyAmount =
    amount !== null && amount !== undefined && amount !== ""
      ? Number(amount).toLocaleString()
      : "0";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm font-semibold tracking-tight text-gray-900">
          {title}
        </p>
        <button
          type="button"
          onClick={removePrice}
          aria-label="Remove price row"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <FaTimes className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <InputField
          title="Price Name"
          type="text"
          value={name}
          onChange={(e) => setPriceName(e.target.value)}
        />
        <InputField
          title="Amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          min="0"
          step="0.01"
          hint={
            <span className="text-[11px] text-gray-600">
              Preview:&nbsp;
              <span className="font-medium">
                {currency || ""} {prettyAmount}
              </span>
            </span>
          }
        />
        <Select code={currency} onChange={(e) => setCurrencyCode(e.target.value)} />
      </div>
    </div>
  );
};

const InputField = ({ type, value, onChange, title, hint, ...rest }: { [x: string]: any }) => {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-gray-800">
        <span className="capitalize">{title}</span>
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={title}
        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-xs placeholder:text-gray-400 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
        {...rest}
      />
      {hint ? <div className="pt-0.5">{hint}</div> : null}
    </div>
  );
};

const Select = ({ onChange, code }: { onChange?: any; code?: any }) => {
  const { currency, fetchCurrencies } = useCurrencyContext();

  useEffect(() => {
    if (!currency) fetchCurrencies();
  }, [currency]);

  let defaultCurrency = undefined;

  if (getCountry()) {
    defaultCurrency = currency?.find(
      (x) =>
        String(x.name).toLowerCase() ===
        String(getCountry()?.country?.name).toLowerCase()
    )?.currency_symbol;
  } else if (getOneLevel().length > 0) {
    defaultCurrency = currency?.find(
      (x) => x?.country_id === getOneLevel()[0]?.data?.data?.country_id
    )?.currency_symbol;
  } else {
    defaultCurrency = currency?.[0]?.currency_symbol;
  }

  return currency ? (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-gray-800">
        Currency
      </label>
      <div className="relative">
        <select
          name="price"
          value={code ? code : defaultCurrency}
          onChange={onChange}
          defaultValue={defaultCurrency || "Select Currency"}
          className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-xs text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
        >
          {currency?.map((curr, key) => (
            <option key={key} value={curr?.currency_symbol}>
              {curr?.currency_symbol} — {curr?.currency_name}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-3 top-3.5 text-gray-500">
          ▾
        </span>
      </div>
    </div>
  ) : null;
};
